import React from "react";
import { useQuery } from "urql";
import { cloneDeep } from "lodash";
import { Link, useLocation, useParams, useSearchParams } from "wouter";
import { sortBy } from "lodash";
import { FileJson, FileSpreadsheet, X } from "lucide-react";
import { Button } from "../../../components/src/Button";

import { useQueryState } from "../../../components/src/use-query-state";
import { DateHelper } from "../../../common/src";
import { formatNumberWithSpaces } from "../../../common/src/utils/FormatFunctions";

import { exportToXlsx, sheetType } from "../../../components/src/common/exportToXlsx";
import { DateForm } from "../../../components/src/dateForm";

import { GET_TRANSFER_AGENT_ORDERS } from "../../../graphql/src/transferagent/queries";
import { TransferAgentOrderForm } from "./TransferAgentOrderForm";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { DefaultColumnFilter, SelectColumnFilter } from "../../../components/src/react-table/ReactTableFilters";
import saveAs from "file-saver";

type OrderGroupPropsType = {
    data: any;
};

export function OrderGroup(props: OrderGroupPropsType): React.ReactElement {
    const [queryArgs] = useSearchParams();
    const downloadExcel = () => {
        const currentRecords: sheetType = orders;
        console.log(currentRecords);
        exportToXlsx([currentRecords], "orders.xlsx");
    };

    const orders = cloneDeep(props.data);

    const downloadJson = () => {
        try {
            const blob = new Blob([JSON.stringify(orders, null, 2)], { type: "application/json" });
            saveAs(blob, "orders.json");
        } catch (e) {
            console.error(e);
        }
    };

    const transferAgentOrders = sortBy(orders, "tradeDate").reverse();

    transferAgentOrders.forEach((order) => {
        order.fundName = order.client.name ? order.client.name : null;
        order.instrumentId = order.instrument._id ? order.instrument._id : null;
        order.instrumentName = order.instrument.name ? order.instrument.name : null;
        order.carryOwn = order.carryOwnTransactionCost ? "True" : "False";
        order.sourceName = order.source.name ? order.source.name : null;
        order.units = order.units ? parseFloat(order.units) : null;
        order.amount = order.amount ? parseFloat(order.amount) : null;
    });

    const columns = React.useMemo(
        () => [
            {
                header: "Id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/transferagentorders/${row.original._id}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`}>
                            {"#"}
                        </Link>
                    );
                },
                size: 20
            },
            {
                header: "Fund",
                accessorKey: "fundName",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <a href={`/parties/${row.original.client._id}`} target={"_blank"}>
                            {cellProps.getValue()}
                        </a>
                    );
                },
                size: 120
            },
            {
                header: "Trade date",
                accessorKey: "tradeDate",
                headerStyle: { textAlign: "right" },
                cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Type",
                accessorKey: "transactionType",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/transferagentorders/${row.original._id}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`}>
                            {cellProps.getValue()}
                        </Link>
                    );
                },
                size: 40
            },
            {
                header: "Instrument",
                accessorKey: "instrumentName",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return row.original.instrumentId ? (
                        <a href={`/instruments/${row.original.instrumentId}`} target={"_blank"}>
                            {cellProps.getValue()}
                        </a>
                    ) : (
                        <div>{cellProps.getValue()}</div>
                    );
                },
                size: 80
            },
            {
                header: "Isin",
                accessorKey: "isin",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return row.original.instrumentId ? (
                        <a href={`/instruments/${row.original.instrumentId}`} target={"_blank"}>
                            {cellProps.getValue()}
                        </a>
                    ) : (
                        <div>{cellProps.getValue()}</div>
                    );
                },
                size: 75
            },
            {
                header: "Status",
                accessorKey: "status",
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Units",
                accessorKey: "units",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>,
                size: 70
            },
            {
                header: "Amount",
                accessorKey: "amount",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>,
                size: 70
            },
            {
                header: "Currency",
                accessorKey: "currency",
                filter: SelectColumnFilter,
                size: 60
            },
            {
                header: "Carry own",
                accessorKey: "carryOwn",
                filter: SelectColumnFilter,
                size: 50
            },
            {
                header: "Value date",
                accessorKey: "valueDate",
                cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Source",
                accessorKey: "sourceName",
                filter: SelectColumnFilter,
                size: 70
            },
            {
                header: "Client label",
                accessorKey: "clientName",
                filter: DefaultColumnFilter,
                size: 150,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/backoffice/customers?clientAccount=${row.original.clientAccount}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                header: "Client number",
                accessorKey: "clientNumber",
                filter: DefaultColumnFilter,
                size: 90,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/backoffice/customers?clientNumber=${row.original.clientNumber}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                header: "Client account",
                accessorKey: "clientAccount",
                filter: DefaultColumnFilter,
                size: 90,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/backoffice/customers?clientAccount=${row.original.clientAccount}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                header: "External id",
                accessorKey: "externalId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/transferagentorders/${row.original._id}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`}>
                            {cellProps.getValue()}
                        </Link>
                    );
                },
                size: 100
            },
            {
                header: "Create timestamp",
                accessorKey: "createTimestamp",
                size: 100
            },
            {
                header: "Update timestamp",
                accessorKey: "updateTimestamp",
                size: 100
            },
            {
                header: "subType",
                accessorKey: "subType",
                Filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "referenceExternalId",
                accessorKey: "referenceExternalId",
                Filter: SelectColumnFilter,
                size: 100
            },
            {
                header: "Net price",
                accessorKey: "netPrice",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>,
                size: 40
            },
            {
                header: "Settled units",
                accessorKey: "settledUnits",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>,
                size: 60
            },
            {
                header: "Settled amount",
                accessorKey: "settledAmount",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>,
                size: 60
            },
            {
                header: "Description",
                accessorKey: "description",
                filter: SelectColumnFilter,
                size: 60
            },
            {
                header: "Updated by",
                id: "updateUserInfo.name",
                accessorFn: (row) => (row.updateUserInfo ? row.updateUserInfo.name : null),
                filter: SelectColumnFilter
            }
        ],
        [queryArgs]
    );

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <p>Total number of orders: {transferAgentOrders.length}</p>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-auto px-2 mb-4" onClick={downloadExcel}>
                    <div style={{ width: "20px", height: "20px", marginBottom: "0.5rem" }}>
                        <FileSpreadsheet className="Excel" />
                    </div>
                </div>
                <div className="w-auto px-2 mb-4" onClick={downloadJson}>
                    <div style={{ width: "20px", height: "20px", marginBottom: "0.5rem" }}>
                        <FileJson style={{ display: "block", width: "20px", height: "20px" }} />
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <ReactTable
                    columns={columns}
                    data={transferAgentOrders}
                    defaultHiddenColumns={[
                        "status",
                        "netPrice",
                        "isin",
                        "currency",
                        "description",
                        "referenceExternalId",
                        "fundName",
                        "updateTimestamp",
                        "clientNumber"
                    ]}
                />
            </div>
        </div>
    );
}

export function TransferAgentOrdersPage(): React.ReactElement {
    const { id }: any = useParams();

    const [, navigate] = useLocation();
    const [queryArgs] = useSearchParams();

    const [tradeDateStart] = useQueryState("tradeDateStart", DateHelper.addMonths(new Date(), -1).toISOString().slice(0, 10));

    const variables = {
        tradeDateStart: queryArgs.get("tradeDate") ? queryArgs.get("tradeDate") : tradeDateStart,
        statusIn: ["Pending", "Deleted", "Confirmed", "Settled"]
    };

    const [{ fetching, error, data }, refetch] = useQuery({ query: GET_TRANSFER_AGENT_ORDERS, variables, requestPolicy: "network-only" });

    return (
        <div>
            <div className="flex">
                <DateForm defaultDateString={tradeDateStart} dateName={"tradeDateStart"}></DateForm>
                <div className="ml-auto">
                    <Button
                        variant="info"
                        type="submit"
                        className="float-right"
                        style={{ marginRight: "2px" }}
                        onClick={() => {
                            const url = `/transferagentorders/new${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`;
                            //console.log(url);
                            navigate(url);
                        }}
                    >
                        New order
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className={id ? "w-full md:w-5/12 lg:w-7/12 xl:w-8/12 2xl:w-9/12 px-2 mb-4" : "w-full px-2 mb-4"}>
                    {fetching && !data ? <div>Loading...</div> : null}
                    {error ? <div>Error! {error.message}</div> : null}
                    {data && data.transferAgentOrders ? <OrderGroup data={data.transferAgentOrders} /> : null}
                </div>

                {id ? (
                    <div className="w-full md:w-7/12 lg:w-5/12 xl:w-4/12 2xl:w-3/12 px-2 mb-4">
                        <Link
                            to={`/transferagentorders${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`}
                            className="close close-btn float-right"
                        >
                            <X />
                        </Link>
                        <TransferAgentOrderForm refetch={refetch}></TransferAgentOrderForm>{" "}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
